/**
 * SCHEDULER REPORT SERVICE - COMPREHENSIVE DOCUMENTATION
 *
 * ===================================================================================
 * PURPOSE
 * ===================================================================================
 * This service provides detailed analytics and forecasting for scheduled content playback.
 * It analyzes schedule rules, expands recurrences, detects conflicts, calculates effective
 * durations based on repeat-type priority overlaps, and generates comprehensive statistics.
 *
 * ===================================================================================
 * REPEAT-TYPE-BASED PRIORITY SYSTEM - **CRITICAL CONCEPT**
 * ===================================================================================
 *
 * **PRIMARY RULE**: Events with different repeat types follow this hierarchy:
 * - Daily (repeat_type 5) = Priority 1 (LOWEST - loses to all others)
 * - Weekly (repeat_type 3) = Priority 2 (loses to Monthly, Yearly, Once)
 * - Hourly (repeat_type 4) = Priority 2 (same as Weekly)
 * - Monthly (repeat_type 2) = Priority 3 (loses to Yearly, Once)
 * - Yearly (repeat_type 1) = Priority 4 (loses to Once)
 * - Once (repeat_type 0) = Priority 5 (HIGHEST - never loses)
 *
 * **SECONDARY RULE**: When two events have the SAME repeat type:
 * - Earlier start time LOSES to later start time
 * - This applies within Daily vs Daily, Weekly vs Weekly, etc.
 *
 * Example 1 - Different Repeat Types:
 * - Rule 1: Daily at 9:00 AM, Duration 8 hours
 * - Rule 2: Weekly at 8:00 AM, Duration 10 hours
 *
 * Result on overlap days:
 * - Rule 1 (Daily) has lower priority than Rule 2 (Weekly)
 * - Rule 1 LOSES all 8 hours → Effective: 0 hours (hidden)
 * - Rule 2 KEEPS full duration → Effective: 10 hours
 *
 * Example 2 - Same Repeat Type (Weekly vs Weekly):
 * - Rule 1: Weekly at 8:00 AM, Duration 8 hours
 * - Rule 2: Weekly at 9:00 AM, Duration 6 hours
 *
 * Result on overlap days:
 * - Both are Weekly (same priority level)
 * - Rule 1 starts earlier → Rule 1 loses overlap
 * - Rule 1: 8 hrs - 6 hrs = 2 hours effective
 * - Rule 2: 6 hours effective (full duration)
 *
 * Why this design?
 * - More specific events (Once, Yearly) override general events (Daily)
 * - Daily "filler" content doesn't override special scheduled events
 * - Users can layer schedules from general (Daily) to specific (Once)
 * - Within same category, later events still override earlier ones
 *
 * ===================================================================================
 * TIME & DURATION STORAGE FORMAT
 * ===================================================================================
 *
 * **ALL times and durations are stored in SECONDS**
 * - event.time: SECONDS from midnight (e.g., 8:00 AM = 28800 seconds)
 * - event.duration: SECONDS (e.g., 12 hours = 43200 seconds)
 * - Conversion: hours = seconds / 3600
 *
 * Example conversions:
 * - 12:00 AM (midnight) = 0 seconds
 * - 06:00 AM = 21600 seconds
 * - 12:00 PM (noon) = 43200 seconds
 * - 11:59 PM = 86340 seconds
 *
 * ===================================================================================
 * RECURRENCE TYPES
 * ===================================================================================
 *
 * 0 = ONCE: Event occurs only on start_date
 * 1 = YEARLY: Repeats annually on specific month/day (uses yearly_offset)
 * 2 = MONTHLY: Repeats on specific day of month (uses monthly_offset 1-31)
 * 3 = WEEKLY: Repeats on specific days of week (uses weekly_days bitmask)
 * 4 = HOURLY: Repeats multiple times per day (uses hourly_offset, repeat_hourly)
 * 5 = DAILY: Repeats every day
 *
 * **Weekly Days Bitmask**:
 * - Bit 0 = Sunday
 * - Bit 1 = Monday
 * - Bit 2 = Tuesday
 * - Bit 3 = Wednesday
 * - Bit 4 = Thursday
 * - Bit 5 = Friday
 * - Bit 6 = Saturday
 *
 * Example: weekly_days = 96 (binary: 1100000) = Saturday + Sunday
 * Check if day enabled: (weekly_days & (1 << dayOfWeek)) !== 0
 *
 * ===================================================================================
 * DURATION CALCULATIONS
 * ===================================================================================
 *
 * **End Time** = Start Time + Duration (capped at 24 hours)
 *
 * **Effective Duration** = Original Duration - Overlap Lost to Later Events
 *
 * **Daily Content Duration** (in statistics) = SUM of all effective durations
 *   NOTE: This is a SUM, not an average!
 *
 * **Utilization Percentage** = (Total Daily Duration / Total Available Minutes) × 100
 *   Where Total Available Minutes = Days in Range × 24 × 60
 *
 * ===================================================================================
 * DATE RANGE SELECTION
 * ===================================================================================
 *
 * 1 Week: Sunday to Saturday (7 days)
 *   - startDate = moment().startOf('week') // Sunday
 *   - endDate = moment().endOf('week') // Saturday
 *
 * 1 Month: Full calendar month
 *   - startDate = moment().startOf('month') // 1st of month
 *   - endDate = moment().endOf('month') // Last day of month
 *
 * 1 Quarter: Full calendar quarter (Q1-Q4)
 *   - startDate = moment().startOf('quarter')
 *   - endDate = moment().endOf('quarter')
 *
 * ===================================================================================
 * CONFLICT DETECTION
 * ===================================================================================
 *
 * A conflict occurs when two or more events have overlapping time ranges on the same date.
 *
 * Overlap condition: (start1 < end2) AND (start2 < end1)
 *
 * Note: Conflicts are detected but do NOT affect playback - the repeat-type priority
 * system automatically resolves overlaps by reducing lower priority events' effective duration.
 *
 * ===================================================================================
 * KEY METHODS
 * ===================================================================================
 *
 * generateReportData(): Main orchestrator
 * expandRecurrences(): Expand recurring events into individual occurrences
 * calculateEffectiveDurations(): Apply repeat-type priority overlap logic
 * getRepeatPriority(): Map repeat type to priority level (1-5)
 * detectConflicts(): Find overlapping events
 * calculateStatistics(): Generate aggregate metrics
 * shouldOccurOnDate(): Check if event occurs on given date based on recurrence type
 * formatTime(): Convert seconds to "HH:MM AM/PM" format with proper rounding
 *
 * ===================================================================================
 * EXAMPLE SCENARIO
 * ===================================================================================
 *
 * Setup:
 * - Rule 1: Weekly (Sat, Sun) at 8:00 AM for 12 hours
 * - Rule 2: Weekly (Mon-Fri) at 10:00 AM for 10 hours
 * - Today: Oct 21, 2025 (Monday)
 * - Report Range: 1 week
 *
 * Expected Results:
 * - Date Range: Oct 20 (Sun) to Oct 26 (Sat)
 * - Oct 20 (Sun): Rule 1 → 12 hours effective
 * - Oct 21-24 (Mon-Thu): Rule 2 → 10 hours each = 40 hours
 * - Oct 25 (Fri): Rule 2 → 10 hours
 * - Oct 26 (Sat): Rule 1 → 12 hours
 * - Total Daily Duration: 74 hours
 * - Utilization: (74 × 60) / (7 × 24 × 60) = 44%
 *
 * ===================================================================================
 */

import { Injectable } from "@angular/core";
import { ISchedule, IScheduleEvent } from "../interfaces/general-interfaces";
import moment from "moment";

export interface ISchedulerStatistics {
    totalEvents: number;
    activeEvents: number;
    inactiveEvents: number;
    sceneCount: number;
    resourceCount: number;
    totalDailyDuration: number;
    averageEventDuration: number;
    earliestTime: string;
    latestTime: string;
    utilizationPercentage: number;
}

export interface IResourceUsage {
    id: string;
    name: string;
    type: "scene" | "resource";
    usageCount: number;
}

export interface IExpandedOccurrence {
    date: string;
    dayOfWeek: string;
    time: string;
    event: IScheduleEvent;
    effectiveDuration?: number; // Duration in minutes, accounting for priority-based overlaps
}

export interface IScheduleConflict {
    date: string;
    time: string;
    events: IScheduleEvent[];
}

export interface IConflictSummary {
    totalConflicts: number;
    affectedDates: number;
    topConflictDates: Array<{ date: string, count: number }>;
    conflictsByTimeSlot: Array<{ time: string, count: number }>;
}

export interface ISchedulerReportData {
    schedule: ISchedule;
    events: IScheduleEvent[];
    statistics: ISchedulerStatistics;
    resourceInventory: IResourceUsage[];
    expandedOccurrences: IExpandedOccurrence[];
    conflicts: IScheduleConflict[];
    conflictSummary: IConflictSummary;
    recurrenceBreakdown: {
        once: IScheduleEvent[];
        daily: IScheduleEvent[];
        weekly: IScheduleEvent[];
        monthly: IScheduleEvent[];
        yearly: IScheduleEvent[];
        hourly: IScheduleEvent[];
    };
}

@Injectable({
    providedIn: "root",
})
export class SchedulerReportService {

    constructor() {
    }

    generateReportData(
        schedule: ISchedule,
        events: IScheduleEvent[],
        daysToExpand: number = 90,
        customStartDate?: moment.Moment,
        customEndDate?: moment.Moment,
    ): ISchedulerReportData {
        const expandedOccurrences = this.expandRecurrences(events, daysToExpand, customStartDate, customEndDate);

        // Calculate effective durations considering priority-based overlaps
        this.calculateEffectiveDurations(expandedOccurrences);

        const conflicts = this.detectConflicts(expandedOccurrences);
        const conflictSummary = this.generateConflictSummary(conflicts);
        const statistics = this.calculateStatistics(events, expandedOccurrences);
        const resourceInventory = this.buildResourceInventory(expandedOccurrences);
        const recurrenceBreakdown = this.breakdownByRecurrence(events);

        return {
            schedule,
            events,
            statistics,
            resourceInventory,
            expandedOccurrences,
            conflicts,
            conflictSummary,
            recurrenceBreakdown,
        };
    }

    expandRecurrences(
        events: IScheduleEvent[],
        daysToExpand: number,
        customStartDate?: moment.Moment,
        customEndDate?: moment.Moment,
    ): IExpandedOccurrence[] {
        const occurrences: IExpandedOccurrence[] = [];
        const startDate = customStartDate ? customStartDate.clone().startOf("day") : moment().startOf("day");
        const endDate = customEndDate ? customEndDate.clone().endOf("day") : moment().add(daysToExpand, "days").endOf("day");

        events.forEach(event => {
            if (event.enabled === "0") return; // Skip inactive events

            const eventStartDate = event.start_date ? moment(event.start_date) : startDate.clone();
            const eventEndDate = event.end_date ? moment(event.end_date) : endDate.clone();

            let currentDate = eventStartDate.clone();
            if (currentDate.isBefore(startDate)) {
                currentDate = startDate.clone();
            }

            while (currentDate.isSameOrBefore(endDate) && currentDate.isSameOrBefore(eventEndDate)) {
                if (this.shouldOccurOnDate(event, currentDate)) {
                    const time = this.formatTime(event.time);

                    // For hourly events, add multiple occurrences
                    if (event.repeat_type === 4) {
                        const hourlyOffset = event.hourly_offset || 1;
                        const repeatCount = event.repeat_hourly || 1;
                        // event.time is in SECONDS, convert to hours
                        const timeInSeconds = typeof event.time === "string" ? parseInt(event.time) : event.time;
                        const startHour = timeInSeconds / 3600;

                        for (let i = 0; i < repeatCount; i++) {
                            const hour = startHour + (i * hourlyOffset);
                            if (hour < 24) {
                                occurrences.push({
                                    date: currentDate.format("YYYY-MM-DD"),
                                    dayOfWeek: currentDate.format("dddd"),
                                    time: this.formatTime(hour), // Pass hours directly
                                    event,
                                });
                            }
                        }
                    } else {
                        occurrences.push({
                            date: currentDate.format("YYYY-MM-DD"),
                            dayOfWeek: currentDate.format("dddd"),
                            time,
                            event,
                        });
                    }
                }

                currentDate.add(1, "day");
            }
        });

        // Sort by date and time (time is stored in SECONDS)
        occurrences.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            if (dateCompare !== 0) return dateCompare;

            // Extract time in seconds from event for proper numeric sorting
            const timeA = typeof a.event.time === "string" ? parseFloat(a.event.time) : a.event.time;
            const timeB = typeof b.event.time === "string" ? parseFloat(b.event.time) : b.event.time;
            return timeA - timeB;
        });

        return occurrences;
    }

    /**
     * Get priority rank for a repeat type.
     * Lower number = lower priority (loses in overlaps)
     * Priority order: Daily (5) < Weekly (3) < Monthly (2) < Yearly (1) < Once (0)
     */
    private getRepeatPriority(repeatType: number): number {
        switch (repeatType) {
            case 5:
                return 1; // Daily - lowest priority
            case 3:
                return 2; // Weekly
            case 2:
                return 3; // Monthly
            case 1:
                return 4; // Yearly
            case 0:
                return 5; // Once - highest priority
            case 4:
                return 2; // Hourly - treat same as Weekly
            default:
                return 1;
        }
    }

    /**
     * Calculate effective durations considering repeat-type-based priority system.
     *
     * Priority Rules:
     * 1. Repeat type hierarchy: Daily (lowest) < Weekly < Monthly < Yearly < Once (highest)
     * 2. Within same repeat type: Earlier start time loses to later start time
     * 3. Overlapped duration is deducted from the lower priority event
     * 4. If duration becomes <= 0, the occurrence is hidden (effectiveDuration = 0)
     *
     * Example:
     * - Rule 1: Daily at 9am for 8hrs (priority 1)
     * - Rule 2: Weekly at 8am for 10hrs (priority 2)
     * - Overlap: 9am to 5pm = 8 hours
     * - Result: Rule 1 duration = 8 - 8 = 0 (hidden), Rule 2 duration = 10 (full)
     */
    calculateEffectiveDurations(occurrences: IExpandedOccurrence[]): void {
        // Group occurrences by date
        const byDate = new Map<string, IExpandedOccurrence[]>();
        occurrences.forEach(occ => {
            if (!byDate.has(occ.date)) {
                byDate.set(occ.date, []);
            }
            byDate.get(occ.date)!.push(occ);
        });

        // For each date, calculate effective durations
        byDate.forEach((dateOccurrences, date) => {
            dateOccurrences.forEach(occ => {
                // Start with full duration
                const timeInSeconds = typeof occ.event.time === "string" ? parseFloat(occ.event.time) : occ.event.time;
                const durationInSeconds = typeof occ.event.duration === "string" ? parseInt(occ.event.duration) : occ.event.duration;

                const startHours = timeInSeconds / 3600;
                const durationHours = (durationInSeconds || 0) / 3600;
                let endHours = startHours + durationHours;

                // Cap at 24 hours
                if (endHours > 24) {
                    endHours = 24;
                }

                // Start with full duration (capped at 24hrs)
                let effectiveHours = endHours - startHours;

                const occPriority = this.getRepeatPriority(occ.event.repeat_type);

                // Check all other occurrences on this date
                dateOccurrences.forEach(other => {
                    if (other === occ) return; // Skip self

                    const otherTimeInSeconds = typeof other.event.time === "string" ? parseFloat(other.event.time) : other.event.time;
                    const otherDurationInSeconds = typeof other.event.duration === "string" ? parseInt(other.event.duration) : other.event.duration;

                    const otherStartHours = otherTimeInSeconds / 3600;
                    const otherDurationHours = (otherDurationInSeconds || 0) / 3600;
                    let otherEndHours = otherStartHours + otherDurationHours;

                    // Cap at 24 hours
                    if (otherEndHours > 24) {
                        otherEndHours = 24;
                    }

                    const otherPriority = this.getRepeatPriority(other.event.repeat_type);

                    // Check if they overlap: (start1 < end2) AND (start2 < end1)
                    if (startHours < otherEndHours && otherStartHours < endHours) {
                        // Calculate overlapping portion
                        const overlapStart = Math.max(startHours, otherStartHours);
                        const overlapEnd = Math.min(endHours, otherEndHours);
                        const overlapHours = overlapEnd - overlapStart;

                        // Determine who loses the overlap
                        let thisEventLoses = false;

                        if (occPriority < otherPriority) {
                            // This event has lower priority (by repeat type) - it loses
                            thisEventLoses = true;
                        } else if (occPriority === otherPriority) {
                            // Same priority level - earlier start time loses
                            if (startHours < otherStartHours) {
                                thisEventLoses = true;
                            }
                        }
                        // If occPriority > otherPriority, this event wins (doesn't lose)

                        if (thisEventLoses) {
                            // Subtract overlap from effective duration
                            effectiveHours -= overlapHours;
                        }
                    }
                });

                // Ensure non-negative
                effectiveHours = Math.max(0, effectiveHours);

                // Convert to minutes and store
                occ.effectiveDuration = Math.round(effectiveHours * 60);
            });
        });
    }

    private shouldOccurOnDate(event: IScheduleEvent, date: moment.Moment): boolean {
        const repeatType = event.repeat_type;

        switch (repeatType) {
            case 0: // Once
                const eventDate = moment(event.start_date || new Date());
                return date.isSame(eventDate, "day");

            case 5: // Daily
                return true;

            case 3: // Weekly
                const weeklyDays = event.weekly_days || 0;
                const dayOfWeek = date.day();
                return (weeklyDays & (1 << dayOfWeek)) !== 0;

            case 2: // Monthly
                const monthlyOffset = event.monthly_offset || 1;
                return date.date() === monthlyOffset;

            case 1: // Yearly
                const yearlyOffset = event.yearly_offset || 0;
                return date.month() === yearlyOffset && date.date() === 1;

            case 4: // Hourly
                return true;

            default:
                return false;
        }
    }

    detectConflicts(occurrences: IExpandedOccurrence[]): IScheduleConflict[] {
        const conflicts: IScheduleConflict[] = [];
        const conflictMap = new Map<string, Set<string>>();

        // Group occurrences by date
        const byDate = new Map<string, IExpandedOccurrence[]>();
        occurrences.forEach(occ => {
            if (!byDate.has(occ.date)) {
                byDate.set(occ.date, []);
            }
            byDate.get(occ.date)!.push(occ);
        });

        // For each date, check for overlapping events
        byDate.forEach((dateOccurrences, date) => {
            for (let i = 0; i < dateOccurrences.length; i++) {
                for (let j = i + 1; j < dateOccurrences.length; j++) {
                    const occ1 = dateOccurrences[i];
                    const occ2 = dateOccurrences[j];

                    // Get start times and durations (stored in SECONDS)
                    const time1InSeconds = typeof occ1.event.time === "string" ? parseFloat(occ1.event.time) : occ1.event.time;
                    const duration1InSeconds = typeof occ1.event.duration === "string" ? parseInt(occ1.event.duration) : occ1.event.duration;
                    const start1 = time1InSeconds / 3600; // Convert to hours
                    const end1 = start1 + (duration1InSeconds || 0) / 3600; // Convert to hours

                    const time2InSeconds = typeof occ2.event.time === "string" ? parseFloat(occ2.event.time) : occ2.event.time;
                    const duration2InSeconds = typeof occ2.event.duration === "string" ? parseInt(occ2.event.duration) : occ2.event.duration;
                    const start2 = time2InSeconds / 3600; // Convert to hours
                    const end2 = start2 + (duration2InSeconds || 0) / 3600; // Convert to hours

                    // Check if they overlap: (start1 < end2) AND (start2 < end1)
                    if (start1 < end2 && start2 < end1) {
                        const conflictKey = `${date}_${Math.min(start1, start2)}`;
                        if (!conflictMap.has(conflictKey)) {
                            conflictMap.set(conflictKey, new Set());
                        }
                        conflictMap.get(conflictKey)!.add(occ1.event.schedule_event_id || "");
                        conflictMap.get(conflictKey)!.add(occ2.event.schedule_event_id || "");

                        // Find or create conflict entry
                        let conflict = conflicts.find(c => c.date === date && c.time === occ1.time);
                        if (!conflict) {
                            conflict = {
                                date,
                                time: this.formatTime(Math.min(start1, start2)),
                                events: [],
                            };
                            conflicts.push(conflict);
                        }

                        // Add events if not already present
                        if (!conflict.events.find(e => e.schedule_event_id === occ1.event.schedule_event_id)) {
                            conflict.events.push(occ1.event);
                        }
                        if (!conflict.events.find(e => e.schedule_event_id === occ2.event.schedule_event_id)) {
                            conflict.events.push(occ2.event);
                        }
                    }
                }
            }
        });

        return conflicts;
    }

    generateConflictSummary(conflicts: IScheduleConflict[]): IConflictSummary {
        const totalConflicts = conflicts.length;

        // Count conflicts by date
        const conflictsByDate = new Map<string, number>();
        conflicts.forEach(conflict => {
            conflictsByDate.set(conflict.date, (conflictsByDate.get(conflict.date) || 0) + 1);
        });

        const affectedDates = conflictsByDate.size;

        // Get top 10 conflict dates
        const topConflictDates = Array.from(conflictsByDate.entries())
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        // Count conflicts by time slot
        const conflictsByTime = new Map<string, number>();
        conflicts.forEach(conflict => {
            conflictsByTime.set(conflict.time, (conflictsByTime.get(conflict.time) || 0) + 1);
        });

        const conflictsByTimeSlot = Array.from(conflictsByTime.entries())
            .map(([time, count]) => ({ time, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        return {
            totalConflicts,
            affectedDates,
            topConflictDates,
            conflictsByTimeSlot,
        };
    }

    calculateStatistics(events: IScheduleEvent[], occurrences: IExpandedOccurrence[]): ISchedulerStatistics {
        const activeEvents = events.filter(e => e.enabled !== "0");
        const totalEvents = events.length;
        const inactiveEvents = totalEvents - activeEvents.length;

        // Count scenes and resources
        let sceneCount = 0;
        let resourceCount = 0;
        events.forEach(event => {
            if (event.event_value) {
                try {
                    const value = JSON.parse(event.event_value);
                    if (value.type === "scene") sceneCount++;
                    else if (value.type === "resource") resourceCount++;
                } catch (e) {
                    // Invalid JSON, skip
                }
            }
        });

        // Calculate average event duration from configured events
        // IMPORTANT: duration is stored in SECONDS, not minutes!
        const durationsInSeconds = activeEvents.map(e => {
            const duration = typeof e.duration === "string" ? parseInt(e.duration) : e.duration;
            return duration || 0;
        });
        const durationsInMinutes = durationsInSeconds.map(d => Math.round(d / 60));
        const totalConfiguredDuration = durationsInMinutes.reduce((sum, d) => sum + d, 0);
        const averageEventDuration = activeEvents.length > 0 ? Math.round(totalConfiguredDuration / activeEvents.length) : 0;

        // Calculate total duration using effective durations (accounting for time-based priority)
        // This is the SUM of all effective durations across all occurrences
        const totalDailyDuration = occurrences.reduce((sum, occ) => {
            return sum + (occ.effectiveDuration || 0);
        }, 0);

        // Find earliest START time and latest END time
        let earliestStartHour = 24;
        let latestEndHour = 0;

        activeEvents.forEach(event => {
            // IMPORTANT: time is in SECONDS since midnight, duration is in SECONDS
            const timeInSeconds = typeof event.time === "string" ? parseInt(event.time) : event.time;
            if (isNaN(timeInSeconds)) return;

            const durationInSeconds = typeof event.duration === "string" ? parseInt(event.duration) : event.duration;
            if (isNaN(durationInSeconds)) return;

            // Convert to hours
            const startHour = timeInSeconds / 3600;
            const durationHours = durationInSeconds / 3600;
            const endHour = startHour + durationHours;

            // Track earliest start and latest end
            if (startHour < earliestStartHour) {
                earliestStartHour = startHour;
            }
            if (endHour > latestEndHour) {
                latestEndHour = endHour;
            }
        });

        // Format times for display (preserve minutes)
        const earliestTime = earliestStartHour < 24 ? this.formatTime(earliestStartHour) : "12:00 AM (midnight)";
        const latestTime = latestEndHour > 0 ? this.formatTime(latestEndHour) : "12:00 AM (midnight)";

        // Calculate utilization (total daily duration / 24 hours * 100)
        const utilizationPercentage = Math.round((totalDailyDuration / 1440) * 100);

        return {
            totalEvents,
            activeEvents: activeEvents.length,
            inactiveEvents,
            sceneCount,
            resourceCount,
            totalDailyDuration,
            averageEventDuration,
            earliestTime,
            latestTime,
            utilizationPercentage,
        };
    }

    buildResourceInventory(occurrences: IExpandedOccurrence[]): IResourceUsage[] {
        const inventory = new Map<string, IResourceUsage>();

        // Count actual occurrences, not just events
        occurrences.forEach(occ => {
            const event = occ.event;
            if (event.event_value) {
                try {
                    const value = JSON.parse(event.event_value);
                    const id = value.id || event.schedule_event_id;
                    const name = value.name || event.event_name;
                    const type = value.type || "resource";

                    if (inventory.has(id)) {
                        inventory.get(id)!.usageCount++;
                    } else {
                        inventory.set(id, {
                            id,
                            name,
                            type: type === "scene" ? "scene" : "resource",
                            usageCount: 1,
                        });
                    }
                } catch (e) {
                    // Invalid JSON, skip
                }
            }
        });

        return Array.from(inventory.values());
    }

    breakdownByRecurrence(events: IScheduleEvent[]) {
        return {
            once: events.filter(e => e.repeat_type === 0),
            yearly: events.filter(e => e.repeat_type === 1),
            monthly: events.filter(e => e.repeat_type === 2),
            weekly: events.filter(e => e.repeat_type === 3),
            hourly: events.filter(e => e.repeat_type === 4),
            daily: events.filter(e => e.repeat_type === 5),
        };
    }

    formatTime(timeValue: string | number): string {
        // timeValue can be either:
        // - SECONDS (when coming from event.time)
        // - HOURS (when coming from calculated values)
        // We need to detect which it is
        let hour = typeof timeValue === "string" ? parseFloat(timeValue) : timeValue;
        if (isNaN(hour)) return "12:00 AM (midnight)";

        // If value is > 24, it's likely in seconds, convert to hours
        if (hour > 24) {
            hour = hour / 3600;
        }

        // Handle hours >= 24 (wrap to next day)
        if (hour >= 24) {
            hour = hour % 24;
        }

        // Extract hours and minutes
        let hourPart = Math.floor(hour);
        let minutePart = Math.round((hour - hourPart) * 60);

        // Handle rounding: if minutes round to 60, roll over to next hour
        if (minutePart >= 60) {
            minutePart = 0;
            hourPart += 1;
        }

        // Handle hour overflow (should not exceed 24)
        if (hourPart >= 24) {
            hourPart = 0;
        }

        // Convert 24-hour to 12-hour format with AM/PM
        const hour12 = hourPart % 12 || 12; // Convert 0 to 12 for midnight
        const ampm = hourPart < 12 ? "AM" : "PM";
        const formattedTime = `${String(hour12).padStart(2, "0")}:${String(minutePart).padStart(2, "0")} ${ampm}`;

        return formattedTime;
    }

    formatDuration(minutes: number): string {
        if (!minutes) return "0 minutes";

        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        const parts: string[] = [];

        if (hours > 0) {
            parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
        }

        if (mins > 0) {
            parts.push(`${mins} minute${mins !== 1 ? "s" : ""}`);
        }

        return parts.join(" ");
    }

    getRecurrenceTypeName(repeatType: number): string {
        const types = ["once", "yearly", "monthly", "weekly", "hourly", "daily"];
        return types[repeatType] || "unknown";
    }

    getDayNames(weeklyDays: number): string[] {
        const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
        const selectedDays: string[] = [];
        for (let i = 0; i < 7; i++) {
            if ((weeklyDays & (1 << i)) !== 0) {
                selectedDays.push(days[i]);
            }
        }
        return selectedDays;
    }
}
